// LeasePaymentReportTable.js
import React, { useState } from "react";
import { Table, Collapse, Button } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const LeasePaymentReportTable = ({ locations }) => {
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const itemsPerPage = 5;

    const toggleExpand = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const paginate = (arr, key) => {
        const currentPage = page[key] || 1;
        const start = (currentPage - 1) * itemsPerPage;
        return arr.slice(start, start + itemsPerPage);
    };

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / itemsPerPage));

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Amount</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations.slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(item => item.payments.length > 0).map((loc) => {
                            const totalAmount = loc.payments.reduce(
                                (acc, p) => acc + (p.amount || 0),
                                0
                            );
                            const key = `loc-${loc.id}`;

                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>${formatCurrency(totalAmount)}</td>
                                        <td>
                                            <Button
                                                className="button-gradient-bg"
                                                onClick={() => toggleExpand(key)}
                                            >
                                                {expanded[key] ? "Hide Payments" : "View Payments"}
                                            </Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={3} className="p-0 border-0">
                                            <Collapse isOpen={expanded[key]}>
                                                <Table
                                                    bordered
                                                    size="sm"
                                                    responsive
                                                    className="mb-0 table-striped"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                            <th>Note</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginate(loc.payments, key).map((p) => (
                                                            <tr key={p.id}>
                                                                <td>{formatDate(p.payment_date)}</td>
                                                                <td>${formatCurrency(p.amount)}</td>
                                                                <td>{p.note || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {loc.payments.length > itemsPerPage && (
                                                    <div className="d-flex justify-content-between align-items-center p-2">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.max(1, (page[key] || 1) - 1),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === 1}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <span>
                                                            Page {page[key] || 1} of {totalPages(loc.payments)}
                                                        </span>
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.min(
                                                                        totalPages(loc.payments),
                                                                        (page[key] || 1) + 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === totalPages(loc.payments)}
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

LeasePaymentReportTable.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            payments: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    date: PropTypes.string.isRequired,
                    amount: PropTypes.number.isRequired,
                    note: PropTypes.string,
                })
            ),
        })
    ).isRequired,
};

export default LeasePaymentReportTable;